import LocallyDropdown from '@components/Product/_LocallyDropdown';
import TierThreeVariations from '@components/Product/Details/_TierThreeVariations';
import Markdown from '@repo/ui/src/components/Markdown';
import React, { useContext } from 'react';
import ShopLocalButton from '@components/Product/CallToActionButton/_ShopLocalButton';
import ProductHeading from '@components/Product/Details/_ProductHeading';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { LinkProps } from '@repo/ui/src/types/link';
import Product from '@models/product';
import { ProductContext } from '@context/ProductProvider';
import CallToActionButton from '@components/Product/CallToActionButton';
import useAddToBasket from '@hooks/basket/useAddToBasket';
import { ButtonVariant } from '@repo/ui/src/components/Button';
import PromotionsAndPricing from '@components/Product/Details/_PromotionsAndPricing';
import CompatibleProducts from '@components/Product/Details/_CompatibleProducts';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { ProductVariation } from '@typings/product';

interface Props {
  essentials?: Array<{
    id: string;
  }>;
  product: Product;
  promoMarkdown?: string;
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  prop65?: {
    title: string;
    warningBody: string;
  };
  compatibleWith?: Array<{
    image: any;
    link?: LinkProps;
  }>;
  isNewTemplate?: boolean;
}

const TierThreeProductSelection = ({ product, compatibleWith }: Props) => {
  const { eCommEnabled } = useCurrentUrl();
  const { trigger } = useAddToBasket();
  const { t } = useTranslation();
  const { totalPrice: _totalPrice, isLoadingAddToCart } = useContext(ProductContext);
  const [quantity, setQuantity] = React.useState(1);
  const [activeVariation, setActiveVariation] = React.useState<ProductVariation>();
  const showPrice = product?.ctaButtonType !== 'noCTA';

  const totalPrice = React.useMemo(() => {
    return _totalPrice && _totalPrice !== '0'
      ? Number(_totalPrice)
      : [product].reduce((val, item) => val + item.price, 0);
  }, [_totalPrice, product]);

  return (
    <div className="tw-grid tw-gap-y-8">
      <div className="tw-gap-y-4 md:tw-grid tw-hidden">
        <ProductHeading
          product={product}
          className="md:tw-block tw-hidden"
          hideDescription
          hideSKU
        />
        {showPrice && (
          <PromotionsAndPricing
            promotions={product?.promotions}
            product={product}
            className="tw-hidden sm:tw-flex"
            isNewTemplate
          />
        )}
        {product.shortDescription && (
          <div className="tw-pt-4">
            <Markdown bodySize="xLarge">{product.shortDescription}</Markdown>
          </div>
        )}
      </div>
      <TierThreeVariations
        product={product}
        activeVariation={activeVariation}
        setActiveVariation={setActiveVariation}
      />
      <div className="tw-grid tw-gap-y-6 md:tw-pr-[5vw]">
        <div className="tw-flex tw-items-stretch">
          <div className="tw-flex tw-flex-grow tw-mr-4">
            <div className="tw-aspect-square tw-w-15 tw-border-neutral-300 tw-border-2 tw-bg-white tw-flex tw-align-center tw-p-1 tw-rounded-md">
              <label className="tra-text-web-body-m tw-text-neutral-500 tw-text-center">
                {t('productDetailPageContent.quantityInputLabel', undefined, {
                  default: 'Qty',
                })}
                <input
                  min={1}
                  max={999}
                  type="number"
                  value={quantity}
                  className="tw-text-neutral-600 tw-justify-center tw-w-full tw-pt-2 tw-text-center"
                  onChange={(e) => {
                    const newQuantity = Number(e.target.value);
                    setQuantity(isNaN(newQuantity) ? 0 : newQuantity);
                  }}
                  data-testid="quantityInput"
                />
              </label>
            </div>
          </div>
          <CallToActionButton
            // @ts-expect-error product !== variation
            product={activeVariation ?? product}
            disable={!activeVariation || isLoadingAddToCart}
            buttonProcessing={isLoadingAddToCart}
            processingText=""
            customProductPrice={totalPrice}
            buttonProps={{
              disabled: !activeVariation || isLoadingAddToCart,
              full: true,
              tabIndex: 0,
              className: isLoadingAddToCart
                ? 'disabled:tw-bg-brand-300'
                : 'disabled:tw-bg-warm-400',
            }}
            onClickAddToCart={() =>
              trigger(
                // @ts-expect-error product !== variation
                [{ ...activeVariation, quantity }],
              )
            }
          />
          {product?.shop?.upc && !product.shop.disableLocally && (
            <LocallyDropdown upc={product.shop.upc} />
          )}
        </div>

        {eCommEnabled && !product.ctaButtonType && (
          <div className="tw-flex tw-justify-center tw-py-3">
            <ShopLocalButton
              icon
              hasLongText
              buttonProps={{
                variant: ButtonVariant.bare,
                className: 'tw-text-neutral-500',
              }}
            />
          </div>
        )}
        {compatibleWith && <CompatibleProducts compatibleWith={compatibleWith} />}
      </div>
    </div>
  );
};

export default TierThreeProductSelection;
